import { isUndefined } from 'lodash-es';
import type { MutableRefObject } from 'react';

import type { RelativeAbsolutePosition } from '../../../../types/pointer-events.js';
import { LARGE_STEP_FACTOR, SMALL_STEP_FACTOR } from '../../constants.js';
import type { InteractionMode, InteractionTriggers } from '../../types.js';

export const panKeyboardEscape = ({
  selectionArea,
  eventCancelled,
  onSelectEnd,
  lastMousePositionRef,
  pinned,
  onHover,
  onTogglePin,
  onModeChange,
  onPanEnd,
}: {
  pinned: boolean;
  eventCancelled: MutableRefObject<boolean>;
  lastMousePositionRef: MutableRefObject<RelativeAbsolutePosition | undefined>;
  selectionArea?: [RelativeAbsolutePosition, RelativeAbsolutePosition];
  onHover?: (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition;
  }) => void;
  onTogglePin?: (event: {
    triggeredBy: InteractionTriggers;
    position: RelativeAbsolutePosition | undefined;
    pin: boolean;
  }) => void;
  onSelectEnd?: (
    event:
      | {
          triggeredBy: InteractionTriggers;
          start: RelativeAbsolutePosition;
          end: RelativeAbsolutePosition;
        }
      | { cancelled: true },
  ) => void;
  onModeChange?: (event: { mode: InteractionMode; keyboard: boolean }) => void;
  onPanEnd?: (event: { triggeredBy: InteractionTriggers }) => void;
}) => {
  onPanEnd?.({ triggeredBy: 'keyboard' });

  if (!isUndefined(selectionArea)) {
    eventCancelled.current = true;
    onSelectEnd?.({ cancelled: true });

    if (lastMousePositionRef.current) {
      onHover?.({
        triggeredBy: 'pointer',
        position: lastMousePositionRef.current,
      });
    }
  }

  if (pinned) {
    onTogglePin?.({
      triggeredBy: 'keyboard',
      pin: false,
      position: lastMousePositionRef.current,
    });
  }

  onModeChange?.({ mode: 'explore', keyboard: false });
};

export const panKeyboardLeft = ({
  shiftKey,
  width,
  onPanChange,
}: {
  shiftKey: boolean;
  width: number;
  onPanChange?: (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => void;
}) => {
  const factor = shiftKey ? -LARGE_STEP_FACTOR : -SMALL_STEP_FACTOR;
  const delta = width * factor;
  onPanChange?.({ triggeredBy: 'keyboard', delta });
};

export const panKeyboardRight = ({
  shiftKey,
  width,
  onPanChange,
}: {
  shiftKey: boolean;
  width: number;
  onPanChange?: (event: {
    triggeredBy: InteractionTriggers;
    delta: number;
  }) => void;
}) => {
  const factor = shiftKey ? LARGE_STEP_FACTOR : SMALL_STEP_FACTOR;
  const delta = width * factor;
  onPanChange?.({ triggeredBy: 'keyboard', delta });
};
